import {
  styled,
  StyledEngineProvider,
  ThemeProvider,
  alpha,
  lighten,
} from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import EmojiReactions from '../EmojiReactions';
import Header from './Header';
import Reactions from './Reactions';
import MessageContent from './MessageContent';
import { COLORS } from 'constants/theme';
import CssBaseline from '@mui/material/ScopedCssBaseline';
import getTheme from 'modules/connect-card-widget/utils/getTheme';
import Box from '@mui/material/Box';
import Button from 'components/Button';
import Typography from '@mui/material/Typography';

const Root = styled('div')`
  max-width: 720px;
  margin: auto;
  header {
    text-align: center;
    margin-bottom: 24px;
    .logo {
      max-width: 140px;
      max-height: 80px;
    }
  }

  .content {
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.4);
    box-shadow: 0px 2.022px 16.696px 0px rgba(0, 0, 0, 0.03),
      0px 1.348px 6px 0px rgba(24, 24, 43, 0.03),
      0px 5px 16px 0px rgba(243, 243, 243, 0.44);
    backdrop-filter: blur(15px);
  }

  .video-container {
    background-color: rgba(0, 0, 0, 0.8);
    margin-bottom: 20px;
  }

  .progress {
    border-radius: 0;
    height: 5px;
  }
  .progress-bar {
    background-color: ${({ theme }) => theme.color};
    transition: width 0.2s ease;
  }

  .fab {
    position: fixed;
    bottom: 20px;
    right: 20px;
  }
`;

const MessageView = ({
  isPreview,
  message,
  onReaction,
  account,
  renderVideo,
  children,
  containerStyle,
  onCTAClick,
}) => {
  const [emojiReactionsVisible, setEmojiReactionsVisible] = useState();
  const brand = isPreview ? account.account_brand : message.account_brand;
  const accountColor = isPreview
    ? account.account_color
    : message.account_color;
  const primaryColor =
    message.template_color || accountColor || COLORS[brand.toUpperCase()];

  const theme = getTheme({
    primary: primaryColor,
    text: message.headline_color,
  });

  useEffect(() => {
    if (emojiReactionsVisible) {
      const timer = setTimeout(() => {
        setEmojiReactionsVisible(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [emojiReactionsVisible]);

  const handleReaction = (emoji) => {
    setEmojiReactionsVisible(emoji.Icon);
    onReaction(emoji);
  };

  return (
    <>
      {emojiReactionsVisible && (
        <EmojiReactions emoji={emojiReactionsVisible} />
      )}
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline
            sx={{
              borderRadius: isPreview && 3,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: -50,
                right: -50,
                height: '25%',
                backgroundColor: lighten(theme.palette.primary.main, 0.6),
                borderBottomLeftRadius: '300%',
                borderBottomRightRadius: '300%',
              }}
            />
            <Box
              sx={{
                py: 6,
                px: 2,
                position: 'relative',
                zIndex: 1,
              }}
            >
              <Root sx={containerStyle}>
                <Header
                  logoPath={
                    isPreview
                      ? message.logo?.location || account.account_logo_url
                      : message.logo?.location
                  }
                  fallbackLogoUrl={message.account_logo_url}
                  accountName={
                    isPreview ? account.account_name : message.account_name
                  }
                />
                <Box className="content" p={3}>
                  <Typography
                    sx={{ mb: 4 }}
                    textAlign="center"
                    variant="h3"
                    color="primary.darker"
                    fontWeight={600}
                  >
                    {message.headline}
                  </Typography>
                  {renderVideo && renderVideo(message)}
                  <MessageContent message={message} onCTAClick={onCTAClick} />
                </Box>
                <Reactions message={message} onReaction={handleReaction} />
                {message.call_to_action_enabled === '1' && (
                  <Box mt={3} textAlign="center">
                    <Button
                      size="large"
                      sx={{ px: 10 }}
                      component="a"
                      onClick={onCTAClick}
                      target={
                        message.is_popup_view === '1' ? '_blank' : '_self'
                      }
                      href={message.call_to_action}
                    >
                      {message.call_to_action_text}
                    </Button>
                  </Box>
                )}
                {children}
              </Root>
            </Box>
          </CssBaseline>
        </ThemeProvider>
      </StyledEngineProvider>
    </>
  );
};

export default MessageView;
